import jsQR from 'jsqr';
import { BrowserQRCodeReader } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';

export type QrDecodeResult = {
  data: string;
  strategy: string;
};

type PreprocessMode =
  | 'original'
  | 'invert'
  | 'grayscale'
  | 'contrast'
  | 'bw'
  | 'adaptive'
  | 'blue'
  | 'descreen';

type BarcodeDetectorLike = {
  detect: (source: ImageBitmapSource) => Promise<Array<{ rawValue?: string }>>;
};

// Configure ZXing to try harder and only look for QR codes. ZXing's grid-sampling
// + Reed-Solomon decoder is far more robust on real-world photos (shadows, glare,
// small modules) than jsQR's pure-JS implementation — it is the same family of
// algorithms Apple's Vision framework uses.
const zxingHints = new Map<DecodeHintType, unknown>();
zxingHints.set(DecodeHintType.TRY_HARDER, true);
zxingHints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]);
const zxingReader = new BrowserQRCodeReader(zxingHints);

// Downscale the aimed region to this size before screen-aware decode. LCD moiré
// is a high-frequency beat between the camera sensor and the till pixel grid;
// downsampling plus a light blur removes it so finder patterns stay intact.
const SCREEN_DECODE_MAX_DIM = 560;

let nativeDetector: BarcodeDetectorLike | null | undefined;

function getNativeDetector(): BarcodeDetectorLike | null {
  if (nativeDetector !== undefined) {
    return nativeDetector;
  }
  const ctor = (globalThis as { BarcodeDetector?: new (opts?: { formats?: string[] }) => BarcodeDetectorLike }).BarcodeDetector;
  if (typeof ctor !== 'function') {
    nativeDetector = null;
    return null;
  }
  try {
    nativeDetector = new ctor({ formats: ['qr_code'] });
  } catch {
    nativeDetector = null;
  }
  return nativeDetector;
}

function otsuThreshold(gray: Uint8ClampedArray): number {
  const hist = new Array(256).fill(0);
  for (let i = 0; i < gray.length; i++) hist[gray[i]]++;
  const total = gray.length;
  let sum = 0;
  for (let t = 0; t < 256; t++) sum += t * hist[t];
  let sumB = 0;
  let wB = 0;
  let maxVar = -1;
  let threshold = 128;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += t * hist[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > maxVar) {
      maxVar = between;
      threshold = t;
    }
  }
  return threshold;
}

function boxBlurGray(gray: Uint8ClampedArray, width: number, height: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(gray.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let sum = 0;
      let n = 0;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = y + dy;
        if (yy < 0 || yy >= height) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx;
          if (xx < 0 || xx >= width) continue;
          sum += gray[yy * width + xx];
          n++;
        }
      }
      out[y * width + x] = Math.round(sum / n);
    }
  }
  return out;
}

function luminanceGray(source: Uint8ClampedArray): Uint8ClampedArray {
  const gray = new Uint8ClampedArray(source.length / 4);
  for (let i = 0, j = 0; i < source.length; i += 4, j++) {
    gray[j] = Math.round(source[i] * 0.299 + source[i + 1] * 0.587 + source[i + 2] * 0.114);
  }
  return gray;
}

function writeGrayToRgba(
  gray: Uint8ClampedArray,
  source: Uint8ClampedArray,
  out: Uint8ClampedArray
): void {
  for (let i = 0, j = 0; i < source.length; i += 4, j++) {
    out[i] = gray[j];
    out[i + 1] = gray[j];
    out[i + 2] = gray[j];
    out[i + 3] = source[i + 3];
  }
}

function thresholdGrayToRgba(
  gray: Uint8ClampedArray,
  source: Uint8ClampedArray,
  out: Uint8ClampedArray,
  cut: number
): void {
  for (let i = 0, j = 0; i < source.length; i += 4, j++) {
    const bw = gray[j] > cut ? 255 : 0;
    out[i] = bw;
    out[i + 1] = bw;
    out[i + 2] = bw;
    out[i + 3] = source[i + 3];
  }
}

function preprocess(
  source: Uint8ClampedArray,
  mode: PreprocessMode,
  width?: number,
  height?: number
): Uint8ClampedArray {
  if (mode === 'original') {
    return source;
  }

  const out = new Uint8ClampedArray(source.length);

  if (mode === 'invert') {
    for (let i = 0; i < source.length; i += 4) {
      out[i] = 255 - source[i];
      out[i + 1] = 255 - source[i + 1];
      out[i + 2] = 255 - source[i + 2];
      out[i + 3] = source[i + 3];
    }
    return out;
  }

  if (mode === 'blue') {
    // Till screens are typically black-on-blue. Standard luminance weights blue
    // at 0.114, so the background collapses toward black and the QR disappears.
    // The blue channel keeps high contrast (background ≈ 200, modules ≈ 0).
    for (let i = 0; i < source.length; i += 4) {
      const b = source[i + 2];
      out[i] = b;
      out[i + 1] = b;
      out[i + 2] = b;
      out[i + 3] = source[i + 3];
    }
    return out;
  }

  const pixelCount = source.length / 4;
  const w = width && width > 0 ? width : Math.round(Math.sqrt(pixelCount));
  const h = height && height > 0 ? height : Math.max(1, Math.floor(pixelCount / w));
  const gray = luminanceGray(source);

  if (mode === 'grayscale') {
    writeGrayToRgba(gray, source, out);
    return out;
  }

  if (mode === 'adaptive') {
    thresholdGrayToRgba(gray, source, out, otsuThreshold(gray));
    return out;
  }

  if (mode === 'descreen') {
    const blurred = w > 2 && h > 2 ? boxBlurGray(gray, w, h) : gray;
    thresholdGrayToRgba(blurred, source, out, otsuThreshold(blurred));
    return out;
  }

  for (let i = 0, j = 0; i < source.length; i += 4, j++) {
    if (mode === 'bw') {
      const bw = gray[j] > 128 ? 255 : 0;
      out[i] = bw;
      out[i + 1] = bw;
      out[i + 2] = bw;
    } else {
      const enhanced = gray[j] < 128 ? Math.max(0, gray[j] - 50) : Math.min(255, gray[j] + 50);
      out[i] = enhanced;
      out[i + 1] = enhanced;
      out[i + 2] = enhanced;
    }
    out[i + 3] = source[i + 3];
  }

  return out;
}

function findBlueBbox(
  rgba: Uint8ClampedArray,
  width: number,
  height: number
): { x: number; y: number; w: number; h: number } | null {
  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;
  let n = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const r = rgba[i];
      const g = rgba[i + 1];
      const b = rgba[i + 2];
      if (b > 90 && b > r + 40 && b > g + 25) {
        n++;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (n < 200) return null;
  const pad = 8;
  const x = Math.max(0, minX - pad);
  const y = Math.max(0, minY - pad);
  return {
    x,
    y,
    w: Math.min(width - x, maxX - minX + 1 + pad * 2),
    h: Math.min(height - y, maxY - minY + 1 + pad * 2),
  };
}

function cropRgba(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  box: { x: number; y: number; w: number; h: number }
): { data: Uint8ClampedArray; width: number; height: number } | null {
  if (box.w < 24 || box.h < 24) return null;
  const out = new Uint8ClampedArray(box.w * box.h * 4);
  for (let row = 0; row < box.h; row++) {
    const src = ((box.y + row) * width + box.x) * 4;
    out.set(rgba.subarray(src, src + box.w * 4), row * box.w * 4);
  }
  return { data: out, width: box.w, height: box.h };
}

function tryJsQrOnBlueRegion(
  imageData: Uint8ClampedArray,
  width: number,
  height: number,
  tag: string
): QrDecodeResult | null {
  const box = findBlueBbox(imageData, width, height);
  if (!box) return null;
  if (box.w >= width * 0.92 && box.h >= height * 0.92) return null;
  const cropped = cropRgba(imageData, width, height, box);
  if (!cropped) return null;
  return tryJsQr(cropped.data, cropped.width, cropped.height, `${tag}:blue-bbox`, true);
}

const QUICK_MODES: PreprocessMode[] = ['original', 'blue', 'adaptive'];
const FULL_MODES: PreprocessMode[] = [
  'original',
  'blue',
  'adaptive',
  'grayscale',
  'descreen',
  'invert',
  'contrast',
  'bw',
];

function tryJsQr(
  imageData: Uint8ClampedArray,
  width: number,
  height: number,
  tag: string,
  quick = false
): QrDecodeResult | null {
  const modes = quick ? QUICK_MODES : FULL_MODES;

  for (const mode of modes) {
    const processed = preprocess(imageData, mode, width, height);
    const code = jsQR(processed, width, height, {
      inversionAttempts: mode === 'original' ? 'attemptBoth' : 'dontInvert',
    });
    if (code?.data) {
      return { data: code.data, strategy: `${tag}:jsqr:${mode}` };
    }
  }

  return null;
}

async function tryZxingCanvas(
  canvas: HTMLCanvasElement,
  tag: string
): Promise<QrDecodeResult | null> {
  try {
    const result = await zxingReader.decodeFromCanvas(canvas);
    const text = result.getText();
    if (text) {
      return { data: text, strategy: `${tag}:zxing` };
    }
  } catch {
    // ZXing throws when no code found — expected during scanning.
  }
  return null;
}

function drawScaledRegion(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  scale: number,
  crop?: { x: number; y: number; w: number; h: number }
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    return null;
  }

  const region = crop ?? { x: 0, y: 0, w: sourceWidth, h: sourceHeight };
  const targetW = Math.max(1, Math.floor(region.w * scale));
  const targetH = Math.max(1, Math.floor(region.h * scale));
  canvas.width = targetW;
  canvas.height = targetH;
  // Smooth scale on purpose for screen sources: it is the cheapest descreen filter.
  ctx.imageSmoothingEnabled = scale < 1;
  ctx.drawImage(
    source,
    region.x,
    region.y,
    region.w,
    region.h,
    0,
    0,
    targetW,
    targetH
  );

  return { canvas, ctx };
}

function downscaleCenter(canvas: HTMLCanvasElement, maxDim = SCREEN_DECODE_MAX_DIM): HTMLCanvasElement | null {
  const w = canvas.width;
  const h = canvas.height;
  if (w < 8 || h < 8) {
    return canvas;
  }
  const crop = {
    x: Math.floor(w * 0.12),
    y: Math.floor(h * 0.18),
    w: Math.floor(w * 0.76),
    h: Math.floor(h * 0.64),
  };
  const scale = Math.min(1, maxDim / Math.max(crop.w, crop.h));
  return drawScaledRegion(canvas, w, h, scale, crop)?.canvas ?? null;
}

function canvasFromProcessed(source: HTMLCanvasElement, mode: PreprocessMode): HTMLCanvasElement | null {
  const ctx = source.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    return null;
  }
  const frame = ctx.getImageData(0, 0, source.width, source.height);
  const processed = preprocess(frame.data, mode, source.width, source.height);
  const out = document.createElement('canvas');
  out.width = source.width;
  out.height = source.height;
  const octx = out.getContext('2d');
  if (!octx) {
    return null;
  }
  octx.putImageData(new ImageData(new Uint8ClampedArray(processed), source.width, source.height), 0, 0);
  return out;
}

async function tryZxingOnScreenVariants(
  canvas: HTMLCanvasElement,
  tag: string
): Promise<QrDecodeResult | null> {
  const working = downscaleCenter(canvas);
  if (!working) {
    return null;
  }

  const variants: Array<{ mode?: PreprocessMode; label: string }> = [
    { label: 'screen' },
    { mode: 'blue', label: 'screen-blue' },
    { mode: 'descreen', label: 'screen-descreen' },
    { mode: 'adaptive', label: 'screen-adaptive' },
    { mode: 'invert', label: 'screen-invert' },
  ];

  for (const variant of variants) {
    const target = variant.mode ? canvasFromProcessed(working, variant.mode) : working;
    if (!target) continue;

    const zxHit = await tryZxingCanvas(target, `${tag}:${variant.label}`);
    if (zxHit) {
      return zxHit;
    }

    const ctx = target.getContext('2d', { willReadFrequently: true });
    if (!ctx) continue;
    const frame = ctx.getImageData(0, 0, target.width, target.height);
    const code = jsQR(frame.data, frame.width, frame.height, { inversionAttempts: 'attemptBoth' });
    if (code?.data) {
      return { data: code.data, strategy: `${tag}:${variant.label}:jsqr` };
    }
  }

  return null;
}

/**
 * Use the browser / OS barcode detector when present (Chrome, Android WebView,
 * some Chromium embeds). This is the closest web equivalent of the iPhone
 * Camera app. Safari on iOS does not expose it — those devices use the
 * screen-aware ZXing / jsQR path below.
 */
export async function decodeQrFromNativeDetector(
  source: CanvasImageSource | ImageBitmap | ImageData,
  tag = 'native'
): Promise<QrDecodeResult | null> {
  const detector = getNativeDetector();
  if (!detector) {
    return null;
  }
  try {
    const codes = await detector.detect(source as ImageBitmapSource);
    const text = codes?.[0]?.rawValue;
    if (text) {
      return { data: text, strategy: `${tag}:barcode-detector` };
    }
  } catch {
    // Detector unavailable for this frame type, or no code in view.
  }
  return null;
}

/**
 * Decode QR from a live camera canvas using ZXing (the robust decoder) directly.
 * Tries the full frame, a center crop, then screen-aware variants (downscale +
 * blue-channel + descreen) for till / phone / PC screens.
 */
export async function decodeQrFromCanvasZxing(
  canvas: HTMLCanvasElement,
  tag = 'camera-zxing'
): Promise<QrDecodeResult | null> {
  const full = await tryZxingCanvas(canvas, `${tag}:full`);
  if (full) {
    return full;
  }

  const w = canvas.width;
  const h = canvas.height;
  if (w > 0 && h > 0) {
    const cropped = drawScaledRegion(
      canvas,
      w,
      h,
      1,
      {
        x: Math.floor(w * 0.15),
        y: Math.floor(h * 0.2),
        w: Math.floor(w * 0.7),
        h: Math.floor(h * 0.6),
      }
    );
    if (cropped) {
      const center = await tryZxingCanvas(cropped.canvas, `${tag}:center`);
      if (center) {
        return center;
      }
    }
  }

  return tryZxingOnScreenVariants(canvas, tag);
}

/**
 * Decode QR from raw RGBA image data (camera frames).
 */
export function decodeQrFromImageData(
  imageData: ImageData,
  tag = 'camera'
): QrDecodeResult | null {
  // Camera quick path: three modes only. Full-res 8-mode + box-blur every
  // 100ms would jank the iPhone preview and drop frames on a till screen.
  const quick = tryJsQr(imageData.data, imageData.width, imageData.height, tag, true);
  if (quick) return quick;
  return tryJsQrOnBlueRegion(imageData.data, imageData.width, imageData.height, tag);
}

/**
 * Decode QR from a loaded image or video frame using jsQR strategies + ZXing fallback.
 */
export async function decodeQrFromImageSource(
  source: CanvasImageSource,
  width: number,
  height: number,
  tag = 'upload'
): Promise<QrDecodeResult | null> {
  const native = await decodeQrFromNativeDetector(source, `${tag}:native`);
  if (native) {
    return native;
  }

  // Full-frame jsQR first. Till photos (blue LCD, compressed camera shots)
  // often decode here immediately. ZXing TRY_HARDER on large canvases can
  // stall the upload path for seconds and look like a failed scan.
  const fullDrawnFirst = drawScaledRegion(source, width, height, 1);
  if (fullDrawnFirst) {
    const frame = fullDrawnFirst.ctx.getImageData(
      0,
      0,
      fullDrawnFirst.canvas.width,
      fullDrawnFirst.canvas.height
    );
    const jsHit = tryJsQr(frame.data, frame.width, frame.height, `${tag}:full@1x`);
    if (jsHit) {
      return jsHit;
    }
    const blueHit = tryJsQrOnBlueRegion(frame.data, frame.width, frame.height, `${tag}:full@1x`);
    if (blueHit) {
      return blueHit;
    }
  }

  const crops = [
    { x: 0, y: 0, w: width, h: height, label: 'full' },
    {
      x: Math.floor(width * 0.1),
      y: Math.floor(height * 0.15),
      w: Math.floor(width * 0.8),
      h: Math.floor(height * 0.55),
      label: 'centre',
    },
  ];

  const scales = height < 500 || width < 500 ? [1, 2, 3, 4] : [1, 0.75, 0.5, 1.5, 0.25, 2];

  for (const crop of crops) {
    for (const scale of scales) {
      const drawn = drawScaledRegion(source, width, height, scale, crop);
      if (!drawn) {
        continue;
      }

      const { canvas, ctx } = drawn;
      const zxHit = await tryZxingCanvas(canvas, `${tag}:${crop.label}@${scale}x`);
      if (zxHit) {
        return zxHit;
      }

      const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const jsHit = tryJsQr(frame.data, frame.width, frame.height, `${tag}:${crop.label}@${scale}x`);
      if (jsHit) {
        return jsHit;
      }
    }
  }

  const fullDrawn = drawScaledRegion(source, width, height, 1);
  if (fullDrawn) {
    const screenHit = await tryZxingOnScreenVariants(fullDrawn.canvas, `${tag}:screen`);
    if (screenHit) {
      return screenHit;
    }
  }

  return null;
}

/**
 * EMVCo Merchant Presented Mode (till / POS / interop QR).
 * Payload Format Indicator tag 00, value 01, then merchant-account templates.
 * iPhone Camera shows these as "0002012626..." — they are not URLs.
 */
export function isEmvCoPaymentQr(rawCode: string): boolean {
  if (!rawCode || typeof rawCode !== 'string') return false;
  const s = rawCode.trim();
  return /^00020[12]/.test(s);
}

/**
 * Client-side guard: does this decoded string look like a PAYMENT QR?
 *
 * The wallet scanner must NOT send every decoded string to the backend — a receipt often has
 * a second QR (survey, receipt ID, etc.) below the payment QR, and a decoder (ZXing)
 * returns the first code it finds, which may be the wrong one. Sending a non-payment
 * string to /qr/validate returns 400 and used to kill the scanner. Silently ignore
 * non-payment decodes and keep scanning.
 *
 * Accepts: EMVCo till payloads, Zapper URLs, JSON {type:payment}, and any URL with
 * payment-like query params or a /pay|/qr|/scan path. Rejects garbled text and random
 * website URLs that the backend would 400 anyway.
 */
export function looksLikePaymentQr(rawCode: string): boolean {
  if (!rawCode || typeof rawCode !== 'string') return false;
  const s = rawCode.trim();
  if (!s) return false;

  if (isEmvCoPaymentQr(s)) return true;
  if (/za\.co\.zapper/i.test(s) || /za\.com\.zapper/i.test(s)) return true;

  if (s.startsWith('{')) {
    try {
      const parsed = JSON.parse(s) as { type?: string; merchant?: unknown; amount?: unknown };
      return parsed?.type === 'payment' || !!parsed?.merchant || !!parsed?.amount;
    } catch {
      return false;
    }
  }

  if (!/^https?:\/\//i.test(s)) return false;

  const lower = s.toLowerCase();

  if (/zap\.pe\b/.test(lower) || /zapper\.com\b/.test(lower)) return true;

  if (/[?&](amount|merchant|pay|qr|invoice|m|tip|total)=/i.test(s)) return true;

  if (/\/(pay|qr|scan|tip)\b/i.test(lower)) return true;

  return false;
}

export function formatQrProcessingError(err: unknown): string {
  let errorMsg = 'Failed to process QR code. ';

  if (err && typeof err === 'object') {
    const apiErr = err as { status?: number; message?: string; payload?: Record<string, unknown> };
    if (typeof apiErr.status === 'number') {
      const status = apiErr.status;
      const payloadMsg =
        typeof apiErr.message === 'string' && apiErr.message
          ? apiErr.message
          : typeof apiErr.payload?.message === 'string'
            ? apiErr.payload.message
            : '';

      const payloadCode =
        typeof apiErr.payload?.code === 'string'
          ? apiErr.payload.code
          : typeof apiErr.payload?.error === 'string'
            ? apiErr.payload.error
            : '';
      if (
        (payloadCode === 'QR_RAIL_DECODE_FAILED' || payloadCode === 'QR_RAIL_INVOICE_UNAVAILABLE') &&
        payloadMsg
      ) {
        return payloadMsg;
      }

      if (status === 400) {
        errorMsg += payloadMsg || 'Invalid QR code format. Please scan a valid payment QR code.';
      } else if (status === 401) {
        errorMsg += 'Please sign in again and retry.';
      } else if (status === 404) {
        errorMsg += 'QR code not recognized. Please ensure you are scanning a valid merchant QR code.';
      } else if (status === 500) {
        errorMsg += 'Server error. Please try again later.';
      } else if (payloadMsg) {
        errorMsg += payloadMsg;
      } else {
        errorMsg += 'Please try again.';
      }
      return errorMsg;
    }

    if ('message' in apiErr && typeof apiErr.message === 'string' && apiErr.message) {
      return errorMsg + apiErr.message;
    }
  }

  return errorMsg + 'Please try again.';
}
